import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { Box, Chip, IconButton, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';

/** Kept in step with lib/feedback.mjs; the server rejects anything it does not recognise. */
const REASONS = {
  up: ['accurate', 'saved me time', 'good sourcing'],
  down: ['wrong or made up', 'missed data it should have found', 'too slow', 'ignored my question', 'unsafe or off-policy']
};
/**
 * A rating is one click. The reason picker is offered after that click and can be dismissed
 * without answering, because a rating that costs a sentence is a rating nobody leaves.
 */
/**
 * Drawn rather than an emoji. An emoji renders in the reader's system font, at their colour, and
 * cannot take a hover or pressed state, so it reads as text in a row of controls.
 */
function ThumbIcon(_ref) {
  let _ref$down = _ref.down,
    down = _ref$down === void 0 ? false : _ref$down,
    _ref$filled = _ref.filled,
    filled = _ref$filled === void 0 ? false : _ref$filled;
  return /*#__PURE__*/React.createElement(Box, {
    component: "svg",
    viewBox: "0 0 24 24",
    "aria-hidden": true,
    sx: {
      width: 17,
      height: 17,
      display: 'block',
      transform: down ? 'rotate(180deg)' : 'none'
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3v11Zm3-11 4.2-8.4A2 2 0 0 1 16 1.5a2.5 2.5 0 0 1 2.4 3.2L17.5 8h3.1a2.4 2.4 0 0 1 2.3 3l-1.7 8a2.4 2.4 0 0 1-2.3 1.9H10V11Z",
    fill: filled ? 'currentColor' : 'none',
    stroke: "currentColor",
    strokeWidth: filled ? 0 : 1.6,
    strokeLinejoin: "round"
  }));
}
export function LivyFeedback(_ref2) {
  var _REASONS;
  let conversationId = _ref2.conversationId,
    turnId = _ref2.turnId,
    surface = _ref2.surface,
    tools = _ref2.tools;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    rating = _useState2[0],
    setRating = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    anchor = _useState4[0],
    setAnchor = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    note = _useState6[0],
    setNote = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    done = _useState8[0],
    setDone = _useState8[1];
  const send = async (value, reason, text) => {
    try {
      await livyJson('/feedback', {
        method: 'POST',
        body: {
          conversationId,
          turnId,
          surface,
          tools,
          rating: value,
          reason,
          note: text || undefined
        }
      });
    } catch (_unused) {
      // A rating that fails to save must not interrupt the person reading the answer.
    }
  };
  const choose = value => e => {
    setRating(value);
    setAnchor(e.currentTarget);
    void send(value);
  };
  const withReason = reason => {
    if (!rating) return;
    if (reason === 'other') return;
    void send(rating, reason);
    setAnchor(null);
    setDone(true);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "2px",
    alignItems: "center"
  }, done && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary',
      mr: '6px'
    }
  }, "Thanks"), [{
    value: 'up',
    label: 'Helpful'
  }, {
    value: 'down',
    label: 'Not helpful'
  }].map(b => {
    const active = rating === b.value;
    return /*#__PURE__*/React.createElement(Tooltip, {
      key: b.value,
      title: b.label
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      "aria-label": b.label,
      "aria-pressed": active,
      onClick: choose(b.value),
      sx: {
        p: '5px',
        borderRadius: '7px',
        color: active ? b.value === 'up' ? 'success.main' : 'error.main' : 'text.secondary',
        opacity: rating && !active ? 0.35 : 1,
        '&:hover': {
          bgcolor: 'action.hover',
          color: b.value === 'up' ? 'success.main' : 'error.main'
        }
      }
    }, /*#__PURE__*/React.createElement(ThumbIcon, {
      down: b.value === 'down',
      filled: active
    })));
  })), /*#__PURE__*/React.createElement(Popover, {
    open: Boolean(anchor),
    anchorEl: anchor,
    onClose: () => setAnchor(null),
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    slotProps: {
      paper: {
        sx: {
          p: '14px',
          width: 320,
          borderRadius: '12px'
        }
      }
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13,
      fontWeight: 600,
      mb: '10px'
    }
  }, rating === 'up' ? 'What was good?' : 'What went wrong?'), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    sx: {
      flexWrap: 'wrap',
      gap: '6px',
      mb: '12px'
    }
  }, ((_REASONS = REASONS[rating !== null && rating !== void 0 ? rating : 'up']) !== null && _REASONS !== void 0 ? _REASONS : []).map(r => /*#__PURE__*/React.createElement(Chip, {
    key: r,
    label: r,
    size: "small",
    variant: "outlined",
    onClick: () => withReason(r),
    sx: {
      cursor: 'pointer'
    }
  }))), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    fullWidth: true,
    multiline: true,
    minRows: 2,
    value: note,
    placeholder: "Anything else (optional)",
    onChange: e => setNote(e.target.value),
    onKeyDown: e => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        void send(rating !== null && rating !== void 0 ? rating : 'up', 'other', note);
        setAnchor(null);
        setDone(true);
      }
    }
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      mt: '8px',
      textAlign: 'right'
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    label: "Send",
    size: "small",
    color: "primary",
    onClick: () => {
      void send(rating !== null && rating !== void 0 ? rating : 'up', note ? 'other' : undefined, note);
      setAnchor(null);
      setDone(true);
    },
    sx: {
      cursor: 'pointer'
    }
  }))));
}